import type {ProjectId} from "../../../metl-models/id/ProjectId.tsx";
import type {BatchOverlapResult} from "../scan-operations/task/BatchOverlapCalculator.tsx";

export const RunTypes: Record<string, keyof TaskSourceBreakdown> = {
    "RUN_JOB": "manual",
    "RUN_JOB_IN_DATABRICKS": "manual",
    "RUN_PROJECT": "manual",
    "RUN_ORCHESTRATION": "manual",
    "CLONE": "manual",
    "RUN_JOB_IN_CLONE": "manual",
    "RUN_ORCHESTRATION_IN_CLONE": "manual",
    "QUEUE_ORCHESTRATION": "queue",
    "API_ORCHESTRATION": "api",
    "SCHEDULE_ORCHESTRATION": "scheduled",
}

export interface TaskSourceBreakdown {
    manual: number
    scheduled: number
    queue: number
    api: number
}

export interface ScanPeriod {
    start: Date
    end: Date
    duration: number
}

export interface TaskOverlaps {
    // The highest number of tasks that are occurring at the same time
    highestOverlap: number

    // The mean number of tasks that are occurring at the same time during a run
    meanOverlap: number

    // The mean number of tasks that are occurring at the same time during the entire span
    meanOverallOverlap: number
}

export interface SingleTaskBatchScanResult {
    // Total time spent for each component implementation ID
    componentImplementationTimes: Record<number, number>
    componentImplementationRowCounts: Record<number, number>
    // number of times each component is run
    componentImplementationRuns: Record<number, number>
    // Total time spent for each job
    jobTimes: Record<string, number>
    // A list of each job that was run at least once in this batch
    jobsRun: string[]
    // Task overlap analysis
    overlaps: TaskOverlaps
    // Overall batch start time
    overallStartTime: number
    // Overall batch end time
    overallEndTime: number
    // Total batch duration
    totalDuration: number

    // Batch metadata
    batchInfo: {
        source: keyof TaskSourceBreakdown
        id: string
        originatorId: string | undefined
        jobName: string
        groupName: string
        projectName: string
        versionName: string
        environmentName: string
        state: string
    }
}

export interface GroupedTaskBatchesScanResult {
    numberOfTaskBatches: number
    scanPeriod: ScanPeriod
    //The total amount of time in which tasks were running
    totalRunningTime: number
    taskSources: TaskSourceBreakdown
    componentImplementationTimes: Record<number, number>
    // Total time spent for each component implementation ID
    componentImplementationRowCounts: Record<number, number>
    componentImplementationRuns: Record<number, number>
    // Total time spent for each job
    jobTimes: Record<string, number>
    // Task overlap analysis
    overlaps: TaskOverlaps

    batchOverlaps?: BatchOverlapResult
}

export interface ProjectTaskBatchesResult extends GroupedTaskBatchesScanResult {
    projectId: ProjectId,
    jobsRun: Record<string, number>
}

export interface OverallTaskHistoryResult {
    projects: ProjectTaskBatchesResult[]
    overall: GroupedTaskBatchesScanResult
    individualResults: SingleTaskBatchScanResult[]
}

/**
 * Utility type for task source analysis
 */
export interface TaskSourceBreakdown {
    manual: number
    scheduled: number
    queue: number
    api: number
}